import { useEffect, useState } from "react";
import { ORPCError } from "@orpc/client";
import { Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";

import { useMessagingEvents } from "@/hooks/use-messaging-events";
import { cn } from "@/lib/utils";
import { orpc } from "@/client";
import { AppSidebar, AppSidebarModal } from "@/components/layout/app-sidebar";
import { AppTopbar } from "@/components/layout/app-topbar";
import { AppMobileNav } from "@/components/layout/app-mobile-nav";
import { AppSearch } from "@/components/layout/app-search";
import { RehearsalBanner } from "@/components/layout/rehearsal-banner";

export const Route = createFileRoute("/_app")({
	beforeLoad: async ({ context }) => {
		const user = await context.queryClient.fetchQuery(orpc.auth.me.queryOptions()).catch((err) => {
			if (err instanceof ORPCError && err.code === "UNAUTHORIZED") {
				redirect({ to: "/login", throw: true });
			}

			if (err instanceof ORPCError && err.code === "PASSWORD_CHANGE_REQUIRED") {
				redirect({ to: "/login", search: { change: true }, throw: true });
			}

			throw err;
		});

		if (user.must_change_password) {
			redirect({ to: "/login", search: { change: true }, throw: true });
		}

		return { user };
	},

	component: AppLayout,
});

function AppLayout() {
	useMessagingEvents();
	const { user } = Route.useRouteContext();
	const conversations = useRouterState({
		select: (state) => state.location.pathname === "/conversas",
	});
	const [menuOpen, setMenuOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);

	useEffect(() => {
		const handleKey = (event: KeyboardEvent) => {
			if (
				(event.ctrlKey || event.metaKey) &&
				event.key.toLowerCase() === "k" &&
				!document.querySelector('[data-component="overlay-shell"]')
			) {
				event.preventDefault();
				setSearchOpen(true);
			}
		};

		document.addEventListener("keydown", handleKey);

		return () => document.removeEventListener("keydown", handleKey);
	}, []);

	return (
		<div className="flex min-h-dvh">
			<a href="#workspace" className="skip-link">
				Ir para o conteúdo
			</a>
			<AppTopbar user={user} onSearch={() => setSearchOpen(true)} />
			<AppSidebar user={user} />
			<AppMobileNav onOpenMenu={() => setMenuOpen(true)} />
			<AppSidebarModal user={user} open={menuOpen} onClose={() => setMenuOpen(false)} />
			{searchOpen && <AppSearch onClose={() => setSearchOpen(false)} />}

			<main
				id="workspace"
				tabIndex={-1}
				className={cn("app-workspace", conversations && "app-conversations")}
			>
				<RehearsalBanner />

				<Outlet />
			</main>
		</div>
	);
}
